import React, {FC, useCallback, useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {skipToken} from '@reduxjs/toolkit/query'
import {useDownloadWalletCardPublicQuery, useDownloadWalletCardPublicGoogleQuery} from '../../../../../services/cellcast.v2'
import './WalletCardDownload.scss'

const WalletCardDownload: FC = () => {
  const {cardId} = useParams<{cardId: string}>()
  const [deviceInfo, setDeviceInfo] = useState({isAndroid: false, isIOS: false})
  const [downloadComplete, setDownloadComplete] = useState(false)
  
  const detect = useCallback(() => {
    const ua = navigator.userAgent.toLowerCase()
    setDeviceInfo({isAndroid: /android/i.test(ua), isIOS: /iphone|ipad|ipod/i.test(ua)})
  }, [])

  useEffect(() => {detect()}, [detect])

  const {data: pkpassBlob, isFetching: loadingIOS} = useDownloadWalletCardPublicQuery(
    cardId && deviceInfo.isIOS ? {id: cardId} : skipToken
  )
  const {data: googleWallet, isFetching: loadingAndroid} = useDownloadWalletCardPublicGoogleQuery(
    cardId && deviceInfo.isAndroid ? {id: cardId} : skipToken
  )

  // Handle IOS file
  useEffect(() => {
    if (deviceInfo.isIOS && pkpassBlob instanceof Blob) {
      const url = window.URL.createObjectURL(pkpassBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'pass.pkpass'
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
        setDownloadComplete(true)
      }, 100)
    }
    if (deviceInfo.isAndroid && googleWallet?.data?.googleWalletUrl) {
      window.location.href = googleWallet.data.googleWalletUrl
      setDownloadComplete(true)
    }
  }, [pkpassBlob, googleWallet, deviceInfo])

  const getSuccessMessage = () => {
    if (deviceInfo.isIOS) {
      return {
        title: "Card Downloaded Successfully!",
        subtitle: "Open your phone wallet to view your card",
      }
    } else if (deviceInfo.isAndroid) {
      return {
        title: "Card Downloaded Successfully!",
        subtitle: "Open your phone wallet to view your card",
      }
    } else {
      return {
        title: "Download Complete!",
        subtitle: "Open your phone wallet to view your card",
      }
    }
  }

  if (downloadComplete) {
    const message = getSuccessMessage()
    
    return (
      <div className='wallet-download-container'>
        <div className='success-message d-flex align-items-center justify-content-center gap-2 flex-column'>
          <div className='icon fs-2'>🎉🎉🎉</div>
          <p className='text-download-success p-0 m-0 fs-3'>
            {message.title}
          </p>
        </div>

        <div className='wallet-card-preview'>
          <div className='wallet-card-inner'>
            <div className='wallet-card-body'>
              <div className='wallet-card-info'>
                <h4>{message.subtitle}</h4>
              </div>
            </div>
          </div>
        </div>

        <div className='confetti'>
          {Array(13)
            .fill('')
            .map((_, i: number) => (
              <div key={i} className='confetti-piece'></div>
            ))}
        </div>
      </div>
    )
  }

  const getLoadingMessage = () => {
    if (deviceInfo.isIOS) {
      return "Preparing Apple Wallet card..."
    } else if (deviceInfo.isAndroid) {
      return "Preparing Google Wallet card..."
    } else {
      return "Preparing your wallet card..."
    }
  }

  return (
    <div className='wallet-download-container'>
      <div className='text-center'>
        <div className='spinner-border text-primary mb-4' role='status'></div>
        <p className='text-download-description'>{getLoadingMessage()}</p>
      </div>
    </div>
  )
}

export default WalletCardDownload 